<template>
  <v-container fluid class="page">
    <div class="page-hero">
      <div>
        <h2 class="d-flex align-center">Stock <HelpHint text="Controla stock físico, reservado y virtual por depósito. Los ajustes impactan disponibilidad de ventas y compras." /></h2>
        <p>Consulta existencias por deposito y producto.</p>
      </div>
      <div class="hero-actions">
        <v-btn color="secondary" class="hero-btn" @click="openBulkUpload">Carga masiva</v-btn>
        <v-btn color="primary" class="hero-btn" @click="openCreate">Nuevo stock</v-btn>
      </div>
    </div>

    <v-alert v-if="successMessage" type="success" dense outlined class="mb-4">
      {{ successMessage }}
    </v-alert>

    <v-tabs v-model="tab" background-color="transparent" class="mb-4">
      <v-tab>Stock</v-tab>
      <v-tab>Depositos</v-tab>
    </v-tabs>

    <v-tabs-items v-model="tab">
      <v-tab-item>
        <v-card outlined class="page-card">
          <div class="table-header">
            <div>
              <h3>Listado</h3>
              <p>{{ stockItems.length }} registros</p>
            </div>
            <div class="filters">
              <v-autocomplete
                v-model="filters.productId"
                :items="productOptions"
                item-text="text"
                item-value="value"
                dense
                outlined
                hide-details
                label="Producto"
                clearable
                :loading="productOptionsLoading"
                @update:search-input="fetchProductOptions"
              />
              <v-autocomplete
                v-model="filters.warehouseId"
                :items="warehouseOptions"
                item-text="text"
                item-value="value"
                dense
                outlined
                hide-details
                label="Depósito"
                clearable
                :loading="warehouseOptionsLoading"
                @update:search-input="fetchWarehouseOptions"
              />
              <v-btn color="primary" :loading="loading" @click="fetchStock">Filtrar</v-btn>
            </div>
          </div>

          <v-data-table
            :headers="headers"
            :items="stockItems"
            :loading="loading"
            class="elevation-0"
            item-key="id"
          >
            <template v-slot:item.quantity="{ item }">
              <span class="font-weight-bold">{{ formatNumber(item.quantity) }}</span>
            </template>
            <template v-slot:item.incoming="{ item }">
              <span class="blue--text">{{ formatNumber(item.incoming || 0) }}</span>
            </template>
            <template v-slot:item.virtual="{ item }">
              <v-chip small color="primary" outlined label>
                {{ formatNumber(Number(item.quantity) - Number(item.reserved || 0) + Number(item.incoming || 0)) }}
              </v-chip>
            </template>
            <template v-slot:item.actions="{ item }">
              <v-btn icon small color="primary" @click="openEdit(item)">
                <v-icon small>mdi-pencil-outline</v-icon>
              </v-btn>
            </template>
          </v-data-table>
        </v-card>
      </v-tab-item>

      <v-tab-item>
        <v-card outlined class="page-card">
          <div class="table-header">
            <div>
              <h3>Depositos</h3>
              <p>{{ warehouses.length }} registrados</p>
            </div>
            <v-btn color="primary" @click="openWarehouseCreate">Nuevo deposito</v-btn>
          </div>

          <v-data-table
            :headers="warehouseHeaders"
            :items="warehouses"
            :loading="warehouseLoading"
            class="elevation-0"
            item-key="id"
          >
            <template v-slot:item.isActive="{ item }">
              <span :class="['pill', item.isActive ? 'pill--success' : 'pill--warn']">
                {{ item.isActive ? 'Activo' : 'Inactivo' }}
              </span>
            </template>
            <template v-slot:item.actions="{ item }">
              <v-btn text small color="primary" @click="openWarehouseEdit(item)">Editar</v-btn>
            </template>
          </v-data-table>
        </v-card>
      </v-tab-item>
    </v-tabs-items>

    <v-dialog v-model="dialog" max-width="520">
      <v-card>
        <v-card-title>{{ isEditing ? 'Editar stock' : 'Crear stock' }}</v-card-title>
        <v-card-text>
          <v-form ref="form" v-model="valid" lazy-validation>
            <v-autocomplete
              v-model="form.productId"
              :items="productOptions"
              item-text="text"
              item-value="value"
              outlined
              dense
              :rules="requiredRules"
              label="Producto"
              clearable
              :loading="productOptionsLoading"
              @update:search-input="fetchProductOptions"
              @change="handleProductChange"
            />
            <template v-if="productSuggestion">
              <v-alert type="info" text outlined density="compact" class="mt-2">
                {{ productSuggestion }}
                <v-btn text small color="primary" class="ml-2" @click="openCreateProduct">Crear artículo</v-btn>
              </v-alert>
            </template>
            <v-autocomplete
              v-model="form.warehouseId"
              :items="warehouseOptions"
              item-text="text"
              item-value="value"
              outlined
              dense
              :rules="requiredRules"
              label="Depósito"
              clearable
              :loading="warehouseOptionsLoading"
              @update:search-input="fetchWarehouseOptions"
              @change="handleWarehouseChange"
            />
            <v-row dense>
              <v-col cols="12">
                <v-alert type="info" dense outlined class="mb-2" v-if="currentStock">
                  <div><strong>Existencia actual:</strong> {{ formatNumber(currentStock.quantity) }}</div>
                </v-alert>
              </v-col>
            </v-row>
            <v-text-field
              v-model.number="form.quantity"
              label="Cantidad a ingresar"
              outlined
              dense
              type="number"
              min="0"
            />
            <v-alert v-if="productSummary" type="info" outlined class="mt-2" density="compact">
              <strong>{{ productSummary.code }}</strong> - {{ productSummary.name }}
              <template v-if="productSummary.unit"> · Unidad: {{ productSummary.unit }}</template>
              <template v-if="productSummary.brand"> · Marca: {{ productSummary.brand }}</template>
            </v-alert>
          </v-form>
          <div v-if="error" class="error-text">{{ error }}</div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="closeDialog">Cancelar</v-btn>
          <v-btn color="primary" :loading="saving" @click="saveStock">Guardar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="warehouseDialog" max-width="480">
      <v-card>
        <v-card-title>{{ warehouseEditing ? 'Editar deposito' : 'Crear deposito' }}</v-card-title>
        <v-card-text>
          <v-form ref="warehouseForm" v-model="warehouseValid" lazy-validation>
            <v-text-field
              v-model="warehouseFormState.code"
              label="Codigo"
              outlined
              dense
              :rules="requiredRules"
            />
            <v-text-field
              v-model="warehouseFormState.name"
              label="Nombre"
              outlined
              dense
              :rules="requiredRules"
            />
            <v-text-field
              v-model="warehouseFormState.description"
              label="Descripcion"
              outlined
              dense
            />
            <v-switch v-model="warehouseFormState.isActive" label="Deposito activo" inset />
          </v-form>
          <div v-if="warehouseError" class="error-text">{{ warehouseError }}</div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="closeWarehouseDialog">Cancelar</v-btn>
          <v-btn color="primary" :loading="warehouseSaving" @click="saveWarehouse">Guardar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="bulkDialog" max-width="720">
      <v-card class="bulk-card">
        <v-card-title class="bulk-header">
          <div>
            <h3>Carga masiva de stock</h3>
            <p>Subí el archivo, verificá los datos y aplicá los cambios con confianza.</p>
          </div>
        </v-card-title>
        <v-card-text>
          <div class="bulk-actions">
            <v-btn color="secondary" outlined :loading="templateLoading" class="bulk-btn" @click="downloadTemplate">
              Descargar plantilla
            </v-btn>
            <v-file-input
              v-model="bulkFile"
              accept=".xlsx,.xls"
              label="Seleccionar archivo"
              outlined
              dense
              hide-details
              show-size
              prepend-icon="mdi-paperclip"
              @change="handleBulkFileChange"
            />
          </div>

          <transition name="fade">
            <div v-if="bulkPreviewTable.length" class="preview-section">
              <div class="preview-title">
                <v-icon color="primary" class="mr-2">mdi-file-table-outline</v-icon>
                <span><strong>Vista previa</strong> · {{ bulkPreviewTable.length }} {{ bulkPreviewTable.length === 1 ? 'fila' : 'filas' }}</span>
              </div>
              
              <div class="preview-table-wrapper">
                <v-simple-table dense class="preview-table themed-table">
                  <thead>
                    <tr>
                      <th v-for="(header, idx) in bulkPreviewHeaders" :key="`h-${idx}`" class="preview-th">
                        {{ header || `Col ${idx + 1}` }}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(row, rIdx) in bulkPreviewTable" :key="`r-${rIdx}`">
                      <td v-for="(cell, cIdx) in row" :key="`c-${rIdx}-${cIdx}`" class="preview-td">
                        {{ cell }}
                      </td>
                    </tr>
                  </tbody>
                </v-simple-table>
              </div>
            </div>
          </transition>

          <v-alert v-if="bulkError" type="error" class="mt-3" outlined>{{ bulkError }}</v-alert>

          <transition name="fade">
            <div
              v-if="bulkPreviewSummary"
              class="summary-section"
              :class="{ 'summary-section--warning': summaryHasErrors }"
            >
              <div class="summary-header">
                <v-icon :color="summaryHasErrors ? 'warning' : 'success'" class="mr-2">
                  {{ summaryHasErrors ? 'mdi-alert-circle-outline' : 'mdi-check-circle-outline' }}
                </v-icon>
                <div class="summary-header-text">
                  <strong>{{ bulkPreviewSummary.message }}</strong>
                  <span>
                    Procesadas {{ summaryProcessedCount }}
                    {{ summaryProcessedCount === 1 ? 'fila' : 'filas' }}
                  </span>
                </div>
              </div>

              <div class="summary-stats">
                <div class="summary-chip">
                  <span>Crear</span>
                  <strong>{{ bulkPreviewSummary.created || 0 }}</strong>
                </div>
                <div class="summary-chip">
                  <span>Actualizar</span>
                  <strong>{{ bulkPreviewSummary.updated || 0 }}</strong>
                </div>
                <div class="summary-chip" :class="{ 'summary-chip--warning': summaryHasErrors }">
                  <span>Errores</span>
                  <strong>{{ bulkPreviewSummary.errors?.length || 0 }}</strong>
                </div>
              </div>

              <div
                v-if="bulkPreviewSummary.details?.length"
                class="summary-section-block"
              >
                <button type="button" class="summary-toggle" @click="summaryDetailsOpen = !summaryDetailsOpen">
                  <span>Detalles ({{ bulkPreviewSummary.details.length }})</span>
                  <v-icon size="18">{{ summaryDetailsOpen ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                </button>
                <v-expand-transition>
                  <div v-if="summaryDetailsOpen" class="summary-table-wrapper">
                    <v-simple-table dense class="summary-table themed-table">
                      <thead>
                        <tr>
                          <th>Fila</th>
                          <th>Producto</th>
                          <th>Depósito</th>
                          <th>Cantidad</th>
                          <th>Acción</th>
                          <th>Cambio</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(detail, idx) in bulkPreviewSummary.details" :key="`detail-${idx}`">
                          <td class="summary-col-index">{{ detail.row }}</td>
                          <td class="summary-col-product">
                            <div class="summary-product-code">{{ detail.productCode }}</div>
                            <div class="summary-product-name">{{ detail.productName }}</div>
                          </td>
                          <td class="summary-col-warehouse">
                            <div class="summary-warehouse-code">{{ detail.warehouseCode }}</div>
                            <div class="summary-warehouse-name">{{ detail.warehouseName }}</div>
                          </td>
                          <td>{{ detail.quantity }}</td>
                          <td>{{ detail.action === 'create' ? 'Alta' : 'Actualización' }}</td>
                          <td>
                            <span v-if="detail.action === 'update'">
                              {{ formatNumber(detail.previousQuantity) }} → {{ formatNumber(detail.newQuantity) }}
                            </span>
                            <span v-else>—</span>
                          </td>
                        </tr>
                      </tbody>
                    </v-simple-table>
                  </div>
                </v-expand-transition>
              </div>

              <div
                v-if="bulkPreviewSummary.errors?.length"
                class="summary-section-block"
              >
                <button type="button" class="summary-toggle" @click="summaryErrorsOpen = !summaryErrorsOpen">
                  <span>Errores ({{ bulkPreviewSummary.errors.length }})</span>
                  <v-icon size="18">{{ summaryErrorsOpen ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                </button>
                <v-expand-transition>
                  <div v-if="summaryErrorsOpen" class="summary-errors-list">
                    <div
                      v-for="(item, idx) in bulkPreviewSummary.errors"
                      :key="`error-${idx}`"
                      class="summary-error-item"
                    >
                      <div class="summary-error-title">
                        Fila {{ item.row }} · {{ item.productCode || 's/ código' }} /
                        {{ item.warehouseCode || 's/ depósito' }}
                      </div>
                      <div class="summary-error-message">{{ item.error }}</div>
                    </div>
                  </div>
                </v-expand-transition>
              </div>
            </div>
          </transition>
        </v-card-text>
        <v-card-actions class="bulk-footer">
          <v-btn text @click="closeBulkDialog" :disabled="bulkLoading || bulkPreviewLoading">Cerrar</v-btn>
          <v-spacer />
          <v-btn
            v-if="!bulkPreviewSummary"
            color="primary"
            class="bulk-btn"
            :disabled="!bulkFile"
            :loading="bulkPreviewLoading"
            @click="previewBulkUpload"
          >
            Validar archivo
          </v-btn>
          <v-btn
            v-else-if="!bulkResult"
            color="primary"
            class="bulk-btn"
            :disabled="bulkPreviewSummary.errors?.length > 0"
            :loading="bulkLoading"
            @click="submitBulkUpload"
          >
            Aplicar cambios
          </v-btn>
          <v-btn
            v-else
            color="success"
            class="bulk-btn"
            disabled
          >
            <v-icon left small>mdi-check-circle</v-icon>
            Cambios aplicados
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { stockService } from "../services/stock";
import { productsService } from "../services/products";
import { warehousesService } from "../services/warehouses";
import * as XLSX from "xlsx";
import HelpHint from "@/components/HelpHint.vue";
import { parseApiError } from "@/utils/errors";

export default {
  name: "StockInventory",
  components: { HelpHint },
  data() {
    return {
      tab: 0,
      stockItems: [],
      warehouses: [],
      loading: false,
      warehouseLoading: false,
      dialog: false,
      warehouseDialog: false,
      bulkDialog: false,
      isEditing: false,
      warehouseEditing: false,
      valid: false,
      warehouseValid: false,
      saving: false,
      warehouseSaving: false,
      error: "",
      warehouseError: "",
      successMessage: "",
      bulkError: "",
      bulkResult: null,
      bulkFile: null,
      bulkLoading: false,
      bulkPreviewLoading: false,
      templateLoading: false,
      bulkPreviewHeaders: [],
      bulkPreviewTable: [],
      bulkPreviewSummary: null,
      bulkPreviewValidated: false,
      summaryDetailsOpen: false,
      summaryErrorsOpen: false,
      productOptions: [],
      productOptionsLoading: false,
      warehouseOptions: [],
      warehouseOptionsLoading: false,
      filters: {
        productId: "",
        warehouseId: "",
      },
      headers: [
        { text: "Producto", value: "productLabel", width: "30%" },
        { text: "Deposito", value: "warehouseLabel" },
        { text: "Físico", value: "quantity", align: "right" },
        { text: "Reservado", value: "reserved", align: "right" },
        { text: "Entrante", value: "incoming", align: "right" },
        { text: "Disponible (Virtual)", value: "virtual", align: "right", sortable: false },
        { text: "", value: "actions", sortable: false, align: "end" },
      ],
      warehouseHeaders: [
        { text: "Codigo", value: "code" },
        { text: "Nombre", value: "name" },
        { text: "Descripcion", value: "description" },
        { text: "Estado", value: "isActive" },
        { text: "Acciones", value: "actions", sortable: false },
      ],
      form: {
        id: "",
        productId: "",
        warehouseId: "",
        quantity: 0,
      },
      currentStock: null,
      productSummary: null,
      productSuggestion: "",
      warehouseFormState: {
        id: "",
        code: "",
        name: "",
        description: "",
        isActive: true,
      },
      requiredRules: [(v) => Boolean(v) || "Campo requerido"],
    };
  },
  computed: {
    summaryHasErrors() {
      return Boolean(this.bulkPreviewSummary?.errors?.length);
    },
    summaryProcessedCount() {
      if (!this.bulkPreviewSummary) return 0;
      const detailsCount = Array.isArray(this.bulkPreviewSummary.details)
        ? this.bulkPreviewSummary.details.length
        : 0;
      if (detailsCount) return detailsCount;
      const created = Number(this.bulkPreviewSummary.created || 0);
      const updated = Number(this.bulkPreviewSummary.updated || 0);
      const errors = Number(this.bulkPreviewSummary.errors?.length || 0);
      const total = created + updated + errors;
      return total || 0;
    },
  },
  created() {
    this.fetchStock();
    this.fetchWarehouses();
    this.fetchWarehouseOptions();
  },
  methods: {
    async fetchStock() {
      this.loading = true;
      this.error = "";
      try {
        const params = {};
        if (this.filters.productId) params.productId = this.filters.productId;
        if (this.filters.warehouseId) params.warehouseId = this.filters.warehouseId;
        const response = await stockService.list(params);
        const items = response.data.data || [];
        this.stockItems = items.map((item) => ({
          ...item,
          productLabel: this.formatProductLabel(item),
          warehouseLabel: this.formatWarehouseLabel(item),
        }));
      } catch (err) {
        this.error = parseApiError(err, "No pudimos cargar el stock.");
      } finally {
        this.loading = false;
      }
    },
    async fetchWarehouses() {
      this.warehouseLoading = true;
      this.warehouseError = "";
      try {
        const response = await warehousesService.list();
        this.warehouses = response.data.data || [];
        const options = this.warehouses.map((w) => ({
          value: w.id,
          text: this.formatWarehouseLabel(w),
        }));
        this.mergeWarehouseOptions(options);
      } catch (err) {
        this.warehouseError = parseApiError(err, "No pudimos cargar los depositos.");
      } finally {
        this.warehouseLoading = false;
      }
    },
    async fetchProductOptions(searchTerm = "") {
      const term = String(searchTerm || "").trim();
      if (term.length > 0 && term.length < 2) {
        return;
      }
      if (!term && this.productOptions.length) {
        return;
      }
      this.productOptionsLoading = true;
      try {
        if (!term) {
          this.productOptionsLoading = false;
          return;
        }
        const response = await productsService.list(term);
        const items = response.data.data || [];
        const options = items.map((product) => ({
          value: product.id,
          text: this.formatProductLabel(product),
        }));
        this.mergeProductOptions(options);
      } catch (err) {
        // ignore, UI will show empty options
      } finally {
        this.productOptionsLoading = false;
      }
    },
    async fetchWarehouseOptions(searchTerm = "") {
      const term = String(searchTerm || "").trim();
      this.warehouseOptionsLoading = true;
      try {
        const response = await warehousesService.list(term || undefined);
        const data = response.data.data || [];
        const options = data.map((warehouse) => ({
          value: warehouse.id,
          text: this.formatWarehouseLabel(warehouse),
        }));
        this.mergeWarehouseOptions(options);
      } catch (err) {
        // ignore errors for autocomplete
      } finally {
        this.warehouseOptionsLoading = false;
      }
    },
    mergeProductOptions(options) {
      const merged = [...options];
      for (const opt of this.productOptions) {
        if (!merged.some((item) => item.value === opt.value)) {
          merged.push(opt);
        }
      }
      this.productOptions = merged;
    },
    mergeWarehouseOptions(options) {
      const merged = [...options];
      for (const opt of this.warehouseOptions) {
        if (!merged.some((item) => item.value === opt.value)) {
          merged.push(opt);
        }
      }
      this.warehouseOptions = merged;
    },
    ensureProductOption(value, label) {
      if (!value) return;
      if (!label) label = value;
      if (!this.productOptions.some((opt) => opt.value === value)) {
        this.productOptions.push({ value, text: label });
      }
    },
    ensureWarehouseOption(value, label) {
      if (!value) return;
      if (!label) label = value;
      if (!this.warehouseOptions.some((opt) => opt.value === value)) {
        this.warehouseOptions.push({ value, text: label });
      }
    },
    formatProductLabel(source) {
      const code = source.code ?? source.productCode ?? "";
      const name = source.name ?? source.productName ?? source.shortDescription ?? "";
      const parts = [];
      if (code) parts.push(code);
      if (name) parts.push(name);
      return parts.join(" - ") || source.id || source.productId || "";
    },
    formatWarehouseLabel(source) {
      const code = source.code ?? source.warehouseCode ?? "";
      const name = source.name ?? source.warehouseName ?? "";
      const parts = [];
      if (code) parts.push(code);
      if (name) parts.push(name);
      return parts.join(" - ") || source.id || source.warehouseId || "";
    },
    openCreate() {
      this.isEditing = false;
      this.dialog = true;
      this.error = "";
      this.form = {
        id: "",
        productId: "",
        warehouseId: "",
        quantity: 0,
      };
      this.fetchProductOptions();
      this.productSummary = null;
      this.productSuggestion = "";
      this.currentStock = null;
    },
    openEdit(item) {
      this.isEditing = true;
      this.dialog = true;
      this.error = "";
      const productLabel = this.formatProductLabel(item);
      const warehouseLabel = this.formatWarehouseLabel(item);
      this.ensureProductOption(item.productId, productLabel);
      this.ensureWarehouseOption(item.warehouseId, warehouseLabel);
      this.form = {
        id: item.id,
        productId: item.productId,
        warehouseId: item.warehouseId,
        quantity: 0,
      };
      this.productSummary = {
        code: item.productCode,
        name: item.productName,
        unit: item.unitCode || null,
        brand: item.brand || null,
      };
      this.productSuggestion = "";
      this.currentStock = {
        quantity: item.quantity,
      };
    },
    closeDialog() {
      this.dialog = false;
    },
    async saveStock() {
      const isValid = this.$refs.form.validate();
      if (!isValid) return;

      this.saving = true;
      this.error = "";
      this.successMessage = "";

      try {
        if (this.isEditing) {
          await stockService.update(this.form.id, {
            quantity: this.form.quantity,
          });
        } else {
          await stockService.create({
            productId: this.form.productId,
            warehouseId: this.form.warehouseId,
            quantity: this.form.quantity,
          });
        }
        this.dialog = false;
        await this.fetchStock();
        this.successMessage = "Stock actualizado correctamente";
      } catch (err) {
        this.error = parseApiError(err, "No pudimos guardar el stock.");
      } finally {
        this.saving = false;
      }
    },
    openWarehouseCreate() {
      this.warehouseEditing = false;
      this.warehouseDialog = true;
      this.warehouseError = "";
      this.warehouseFormState = {
        id: "",
        code: "",
        name: "",
        description: "",
        isActive: true,
      };
    },
    openWarehouseEdit(item) {
      this.warehouseEditing = true;
      this.warehouseDialog = true;
      this.warehouseError = "";
      this.warehouseFormState = {
        id: item.id,
        code: item.code,
        name: item.name,
        description: item.description || "",
        isActive: Boolean(item.isActive),
      };
    },
    closeWarehouseDialog() {
      this.warehouseDialog = false;
    },
    async saveWarehouse() {
      const isValid = this.$refs.warehouseForm.validate();
      if (!isValid) return;

      this.warehouseSaving = true;
      this.warehouseError = "";

      try {
        if (this.warehouseEditing) {
          await warehousesService.update(this.warehouseFormState.id, {
            name: this.warehouseFormState.name,
            description: this.warehouseFormState.description,
            isActive: this.warehouseFormState.isActive,
          });
        } else {
          await warehousesService.create({
            code: this.warehouseFormState.code,
            name: this.warehouseFormState.name,
            description: this.warehouseFormState.description,
            isActive: this.warehouseFormState.isActive,
          });
        }
        this.warehouseDialog = false;
        await this.fetchWarehouses();
      } catch (err) {
        this.warehouseError = parseApiError(err, "No pudimos guardar el deposito.");
      } finally {
        this.warehouseSaving = false;
      }
    },
    async handleProductChange(productId) {
      if (!productId) {
        this.productSummary = null;
        this.productSuggestion = "";
        return;
      }
      try {
        const response = await productsService.get(productId);
        const product = response.data.data;
        if (product) {
          this.productSummary = {
            code: product.code,
            name: product.name,
            unit: product.unitCode,
            brand: product.brand,
          };
          this.productSuggestion = "";
        } else {
          this.productSummary = null;
        }
      } catch (err) {
        this.productSummary = null;
      }
    },
    handleWarehouseChange(warehouseId) {
      if (!warehouseId) return;
      const option = this.warehouseOptions.find((item) => item.value === warehouseId);
      if (!option) {
        this.fetchWarehouseOptions();
      }
      if (this.form.productId && warehouseId) {
        this.loadCurrentStock(this.form.productId, warehouseId);
      }
    },
    async loadCurrentStock(productId, warehouseId) {
      try {
        const response = await stockService.list({ productId, warehouseId });
        const item = (response.data.data || [])[0];
        if (item) {
          this.currentStock = {
            quantity: item.quantity,
          };
        } else {
          this.currentStock = null;
        }
      } catch (err) {
        this.currentStock = null;
      }
    },
    async handleProductChange(productId) {
      if (!productId) {
        this.productSummary = null;
        this.productSuggestion = "";
        this.currentStock = null;
        return;
      }
      try {
        const response = await productsService.get(productId);
        const product = response.data.data;
        if (product) {
          this.productSummary = {
            code: product.code,
            name: product.name,
            unit: product.unitCode,
            brand: product.brand,
          };
          this.productSuggestion = "";
        } else {
          this.productSummary = null;
        }
      } catch (err) {
        this.productSummary = null;
      }

      // intentar auto seleccionar depósito existente
      try {
        const listResponse = await stockService.list({ productId });
        const firstItem = (listResponse.data.data || [])[0];
        if (firstItem) {
          this.ensureWarehouseOption(firstItem.warehouseId, this.formatWarehouseLabel(firstItem));
          this.form.warehouseId = firstItem.warehouseId;
          this.loadCurrentStock(productId, firstItem.warehouseId);
          return;
        }
      } catch (err) {
        // ignorar
      }
      this.currentStock = null;
    },
    openBulkUpload() {
      this.bulkDialog = true;
      this.resetBulkState();
    },
    closeBulkDialog() {
      if (this.bulkLoading) return;
      this.bulkDialog = false;
    },
    resetBulkState() {
      this.bulkFile = null;
      this.bulkError = "";
      this.bulkResult = null;
      this.bulkPreviewSummary = null;
      this.bulkPreviewHeaders = [];
      this.bulkPreviewTable = [];
      this.bulkPreviewValidated = false;
      this.summaryDetailsOpen = false;
      this.summaryErrorsOpen = false;
    },
    handleBulkFileChange(file) {
      this.bulkError = "";
      this.bulkResult = null;
      this.bulkPreviewSummary = null;
      this.bulkPreviewValidated = false;
      this.bulkPreviewHeaders = [];
      this.bulkPreviewTable = [];
      this.summaryDetailsOpen = false;
      this.summaryErrorsOpen = false;

      if (!file) return;

      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const data = new Uint8Array(event.target.result);
          const workbook = XLSX.read(data, { type: "array" });
          const sheetName = workbook.SheetNames[0];
          if (!sheetName) {
            this.bulkError = "El archivo no contiene hojas.";
            return;
          }
          const worksheet = workbook.Sheets[sheetName];
          const rows = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: "" });
          if (!rows.length) {
            this.bulkError = "El archivo está vacío.";
            return;
          }
          this.bulkPreviewHeaders = rows[0];
          // Filtrar solo filas con datos (no vacías)
          const dataRows = rows.slice(1).filter(row => 
            row && row.some(cell => cell !== null && cell !== undefined && cell !== "")
          );
          // Tomar máximo 50 filas para previsualización
          this.bulkPreviewTable = dataRows.slice(0, 50);
        } catch (error) {
          console.error("Error leyendo el archivo:", error);
          this.bulkError = "No pudimos leer el archivo.";
        }
      };
      reader.readAsArrayBuffer(file);
    },
    async previewBulkUpload() {
      if (!this.bulkFile) return;
      this.bulkPreviewLoading = true;
      this.bulkError = "";
      this.bulkPreviewSummary = null;
      this.bulkPreviewValidated = false;
      try {
        const formData = new FormData();
        formData.append("file", this.bulkFile);
        const response = await stockService.upload(formData, { preview: true });
        this.bulkPreviewSummary = response.data.data;
        this.bulkPreviewValidated = !this.bulkPreviewSummary.errors?.length;
        this.summaryDetailsOpen = Boolean(this.bulkPreviewSummary.details?.length);
        this.summaryErrorsOpen = Boolean(this.bulkPreviewSummary.errors?.length);
      } catch (err) {
        this.bulkError = parseApiError(err, "No pudimos validar el archivo.");
      } finally {
        this.bulkPreviewLoading = false;
      }
    },
    async downloadTemplate() {
      this.templateLoading = true;
      try {
        const response = await stockService.downloadTemplate();
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "stock_template.xlsx");
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (err) {
        this.bulkError = parseApiError(err, "No pudimos descargar la plantilla.");
      } finally {
        this.templateLoading = false;
      }
    },
    async submitBulkUpload() {
      if (!this.bulkFile) return;

      // Si aún no se validó, primero correr preview
      if (!this.bulkPreviewValidated) {
        await this.previewBulkUpload();
        if (!this.bulkPreviewValidated) {
          return;
        }
      }

      this.bulkLoading = true;
      this.bulkError = "";
      this.bulkResult = null;
      try {
        const formData = new FormData();
        formData.append("file", this.bulkFile);
        const response = await stockService.upload(formData);
        this.bulkResult = response.data.data;
        this.bulkPreviewSummary = response.data.data;
        this.bulkPreviewValidated = true;
        this.summaryDetailsOpen = Boolean(this.bulkPreviewSummary.details?.length);
        this.summaryErrorsOpen = Boolean(this.bulkPreviewSummary.errors?.length);
        await this.fetchStock();
      } catch (err) {
        this.bulkError = parseApiError(err, "No pudimos procesar el archivo.");
      } finally {
        this.bulkLoading = false;
      }
    },
    openCreateProduct() {
      this.$router.push({ name: "stock-catalog" });
    },
    formatNumber(value) {
      return new Intl.NumberFormat("es-AR", { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(value || 0);
    },
  },
};
</script>

<style scoped>
.page {
  padding: 0;
}

.page-hero {
  background: #ffffff;
  border-radius: 18px;
  padding: 20px 24px;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.hero-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.page-hero h2 {
  margin: 0 0 4px;
  font-size: 22px;
  font-weight: 700;
}

.page-hero p {
  margin: 0;
  color: #64748b;
}

.hero-btn {
  text-transform: none;
  font-weight: 700;
}

.page-card {
  border-radius: 18px;
  padding: 16px 20px 8px;
}

.table-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
}

.table-header h3 {
  margin: 0 0 4px;
  font-size: 18px;
  font-weight: 700;
}

.table-header p {
  margin: 0;
  color: #64748b;
  font-size: 12px;
}

.filters {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.pill {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
}

.pill--success {
  background: rgba(22, 163, 74, 0.12);
  color: #16a34a;
}

.pill--warn {
  background: rgba(245, 158, 11, 0.12);
  color: #f59e0b;
}

.error-text {
  margin-top: 12px;
  color: #dc2626;
}

.bulk-subtitle {
  margin-bottom: 12px;
  color: #475569;
}

.bulk-actions {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.bulk-card {
  display: flex;
  flex-direction: column;
  max-height: 90vh;
  overflow-y: auto;
  overflow-x: hidden;
}

.bulk-header h3 {
  margin: 0 0 4px;
  font-size: 20px;
  font-weight: 700;
}

.bulk-header p {
  margin: 0;
  color: var(--text-tertiary);
  font-size: 14px;
}

.bulk-card .v-card-text {
  flex: 0 0 auto;
  overflow: visible;
  min-height: auto;
  padding-bottom: 8px;
}

.preview-section {
  margin-top: 24px;
  border: 1px solid var(--border-color);
  border-radius: 12px;
  overflow: hidden;
  background: var(--bg-secondary);
}

.preview-title {
  display: flex;
  align-items: center;
  padding: 14px 20px;
  background: var(--bg-tertiary);
  border-bottom: 1px solid var(--border-color);
  font-size: 14px;
  color: var(--text-primary);
}

.preview-table-wrapper {
  max-height: 220px;
  overflow: auto;
}

.preview-table {
  background: transparent !important;
}

.preview-th {
  background: var(--bg-tertiary) !important;
  color: var(--text-primary) !important;
  font-weight: 600 !important;
  font-size: 13px !important;
  padding: 12px 16px !important;
  border-bottom: 2px solid var(--border-color) !important;
  white-space: nowrap;
  position: sticky;
  top: 0;
  z-index: 1;
}

.preview-td {
  color: var(--text-secondary) !important;
  font-size: 13px !important;
  padding: 10px 16px !important;
  border-bottom: 1px solid var(--border-color) !important;
}

.summary-wrapper {
  margin-top: 16px;
}

.bulk-footer {
  padding: 14px 20px;
  border-top: 1px solid var(--border-color);
  background: var(--bg-secondary);
  position: sticky;
  bottom: 0;
  z-index: 3;
}

.bulk-btn {
  text-transform: none;
  font-weight: 600;
}

.summary-section {
  margin-top: 24px;
  border: 1px solid rgba(34, 197, 94, 0.35);
  background: rgba(34, 197, 94, 0.08);
  border-radius: 12px;
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.summary-section--warning {
  border-color: rgba(250, 204, 21, 0.45);
  background: rgba(250, 204, 21, 0.12);
}

.summary-header {
  display: flex;
  align-items: center;
  gap: 12px;
}

.summary-header-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  color: var(--text-primary);
  font-size: 14px;
}

.summary-header-text span {
  font-size: 13px;
  color: var(--text-secondary);
}

.summary-stats {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.summary-chip {
  display: inline-flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px 12px;
  border-radius: 10px;
  background: rgba(59, 130, 246, 0.1);
  color: var(--text-secondary);
  min-width: 110px;
}

.summary-chip strong {
  font-size: 16px;
  color: var(--text-primary);
}

.summary-chip--warning {
  background: rgba(250, 204, 21, 0.2);
  color: #92400e;
}

.summary-section-block {
  background: var(--bg-secondary);
  border-radius: 12px;
  border: 1px solid var(--border-color);
  padding: 12px 14px;
}

.summary-toggle {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  border: none;
  background: transparent;
  color: var(--text-primary);
  font-size: 14px;
  font-weight: 600;
  padding: 4px 0;
  cursor: pointer;
}

.summary-toggle:hover {
  color: var(--accent-primary);
}

.summary-table-wrapper {
  margin-top: 12px;
  border-radius: 10px;
  border: 1px solid var(--border-color);
  overflow: auto;
  max-height: 240px;
}

.summary-table {
  background: transparent !important;
}

.summary-table tbody tr:last-child td {
  padding-bottom: 18px !important;
}

.summary-table th {
  background: var(--bg-tertiary) !important;
  color: var(--text-primary) !important;
  font-weight: 600 !important;
  font-size: 13px !important;
  padding: 10px 12px !important;
  border-bottom: 2px solid var(--border-color) !important;
}

.summary-table td {
  color: var(--text-secondary) !important;
  font-size: 13px !important;
  padding: 10px 12px !important;
  border-bottom: 1px solid var(--border-subtle) !important;
}

.summary-col-index {
  width: 60px;
  font-weight: 600;
  color: var(--text-primary) !important;
}

.summary-col-product,
.summary-col-warehouse {
  min-width: 180px;
}

.summary-product-code,
.summary-warehouse-code {
  font-weight: 600;
  color: var(--text-primary);
}

.summary-product-name,
.summary-warehouse-name {
  font-size: 12px;
  color: var(--text-tertiary);
}

.summary-errors-list {
  margin-top: 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-height: 160px;
  overflow: auto;
}

.summary-error-item {
  border-radius: 10px;
  border: 1px solid rgba(239, 68, 68, 0.35);
  background: rgba(254, 226, 226, 0.6);
  padding: 10px 12px;
}

.summary-error-title {
  font-weight: 600;
  font-size: 13px;
  color: #991b1b;
}

.summary-error-message {
  font-size: 12px;
  color: #b91c1c;
  margin-top: 4px;
}

@media (max-width: 960px) {
  .page-hero {
    flex-direction: column;
    align-items: flex-start;
  }
  .table-header {
    flex-direction: column;
    align-items: flex-start;
  }
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter, .fade-leave-to {
  opacity: 0;
}
</style>
